import { type ChangeEvent, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { NavbarItem } from '@accure/ui';
import { TriangleAlert } from 'lucide-react';

import { appConfig } from '@/config';
import { useLookerEnv } from '@/hooks/looker/use-looker-env';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { LookerEnv, LookerEnvSchema } from '@/types/looker-env';
import { cls } from '@/utils/cls';

// Define pages that use Looker dashboards
const LOOKER_PAGES = [
  'benchmark',
  'details',
  'warranty',
  'data-compatibility',
  // Add any other pages that use Looker dashboards
];

const lookerEnvLabels: Record<LookerEnv, string> = {
  [LookerEnv.STAGING]: 'Staging',
  [LookerEnv.PRODUCTION]: 'Production',
};

const lookerEnvValues = Object.values(LookerEnv);

export function LookerEnvSelector() {
  const me = useMe();
  const featureFlag = useFeatureFlag();
  const [lookerEnv, setLookerEnv] = useLookerEnv();
  const location = useLocation();

  // Check if current page uses Looker
  const isLookerPage =
    // INFO: /warranty when it's v3 isn't a looker page
    !(featureFlag('warrantyV3') && location.pathname.includes('/warranty')) &&
    LOOKER_PAGES.some((page) => location.pathname.includes(`/${page}`));

  const handleLookerEnvChange = useCallback(
    async function (e: ChangeEvent<HTMLSelectElement>) {
      const newLookerEnv = await LookerEnvSchema.safeParseAsync(e.target.value);

      if (newLookerEnv.success) {
        setLookerEnv(newLookerEnv.data);
      }
    },
    [setLookerEnv],
  );

  // Only show for internal users on Looker pages
  if (!me.isInternal || !isLookerPage) {
    return null;
  }

  // Check if the selected environment doesn't match the app environment
  const isEnvironmentMismatch = appConfig.defaultLookerEnv !== lookerEnv;

  return (
    <NavbarItem>
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-500">Data Env:</span>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={lookerEnv}
              onChange={handleLookerEnvChange}
              className={cls(
                'appearance-none rounded-md border-0 bg-transparent py-1 pl-3 pr-8 focus:outline-none focus:ring-0',
                isEnvironmentMismatch && 'font-medium text-amber-600',
              )}
              style={{ minWidth: '110px' }}
            >
              {lookerEnvValues.map((value) => (
                <option key={value} value={value}>
                  {lookerEnvLabels[value]}
                </option>
              ))}
            </select>
          </div>

          {isEnvironmentMismatch ? (
            <div
              className="flex cursor-help items-center"
              title={`App is in ${appConfig.defaultLookerEnv} but viewing ${lookerEnv} data`}
            >
              <span className="flex size-5 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <TriangleAlert className="size-4" />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </NavbarItem>
  );
}
